/**
 * World & Session Management Component
 * Admin view for creating/editing/deleting worlds, resetting a world's save, and managing sessions per world.
 */

import { formatDateTime } from '../../utils/format-date';
import { instantFromInputs, localDateInput, localTimeInput } from '../../utils/date-input';
import { saveFailure, type FormFailure } from './save-failure-words';
import { useMessageInSight } from '../../components/use-message-in-sight';
import { EmptyState } from '../../components/empty-state';
import { SearchInput } from '../../components/search-input';
import { Modal } from '../../components/modal';
import { Table } from '../../components/table';
import { CheckRow, Checkbox, FormBlock, FormField, FormMessage, Input, RequiredMark, Select } from '../../components/form-field';
import { activeTone } from './status-tone';
import { Pill } from '../../components/pill';
import { useState } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../shell/common-header';
import type {
  WorldItem,
  SessionItem,
  UpsertSessionPayload,
  LocalSaveItem,
  SessionScriptItem,
} from '../../api/admin/sessions/types';
import {
  estimateSessionScriptDurationMinutes,
  isScriptDriven,
  sessionLengthMinutes,
} from '../../api/admin/sessions/helpers';
import { SESSION_SCRIPT_FALLBACK_DURATION_MINUTES } from '../../api/_shared/session-timing';
import { SessionTimeline } from './session-timeline';
import { type CohortItem } from '../../api/admin/cohorts/types';
import {
  useAdminGameList,
  useAdminLocalSaves,
  useAdminScripts,
  useCreateSession,
  useCreateWorld,
  useUpdateSession,
  useUpdateWorld,
} from '../../api/admin/sessions/hooks';
import { getGameClientUrl } from '../../config/environment';
import { currentUiLanguage } from '../../utils/launch-url';
import { useWorldSessionBrowser, type WorldStatusFilter } from './use-world-session-browser';
import { getSessionStatus } from './session-status';
import { SessionStatusPill } from './session-status-pill';
import { configForStartingSetup, startingSetupForConfig, type WorldStartingSetup } from './world-starting-setup';
import admin from './admin-dashboard.module.css';

interface WorldSessionManagementProps {
  onBack: () => void;
}

export function WorldSessionManagement({ onBack }: WorldSessionManagementProps) {
  const { t } = useTranslation();
  // Which world is being looked at, and what is being edited about it.
  const {
    worlds,
    filteredWorlds,
    cohorts,
    isLoadingWorlds,
    selectedWorld,
    sessions,
    isLoadingSessions,
    interruptedSessionIds,
    error,
    showCreateWorldForm,
    setShowCreateWorldForm,
    editingWorld,
    setEditingWorld,
    showCreateSessionForm,
    setShowCreateSessionForm,
    editingSession,
    setEditingSession,
    worldSearch,
    setWorldSearch,
    worldStatusFilter,
    setWorldStatusFilter,
    handleSelectWorld,
    handleDeleteWorld,
    handleResetWorldSave,
    resettingWorldId,
    handleWorldSaved,
    handleDeleteSession,
    handleSessionSaved,
  } = useWorldSessionBrowser();

  return (
    <div className="portal-shell">
      <CommonHeader
        title={t('admin.worlds.title')}
        showBackButton
        onBackClick={onBack}
      />

      <main className={admin.adminPanelMain}>
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

        {/* ── Worlds table ── */}
        <div className={admin.adminTableWrapper}>
          <div className={admin.adminTableHeader}>
            <h2 className={admin.adminTableTitle}>
              {t('admin.worlds.totalCount', { count: worlds.length })}
            </h2>
            <div className={admin.adminFilterBar}>
              <Select
                className={admin.adminFilter}
                aria-label={t('common.filter')}
                value={worldStatusFilter}
                onChange={(e) => setWorldStatusFilter(e.target.value as WorldStatusFilter)}
              >
                <option value="">{t('common.filterAll')}</option>
                <option value="active">{t('common.filterActive')}</option>
                <option value="inactive">{t('common.filterInactive')}</option>
              </Select>
              <Button type="button" onClick={() => setShowCreateWorldForm(true)}
              >
                + {t('admin.worlds.createWorld')}
              </Button>
            </div>
          </div>
          {!selectedWorld && worlds.length > 0 && (
            <div className={admin.adminWorldsHint}>
              👆 {t('admin.worlds.clickRowHint')}
            </div>
          )}
          {isLoadingWorlds ? (
            <div className={admin.adminLoading}>{t('common.loading')}</div>
          ) : worlds.length === 0 ? (
            !error && <EmptyState icon="🌍">{t('admin.worlds.noWorlds')}</EmptyState>
          ) : (
            <>
              <div className={admin.adminSearchBar}>
                <SearchInput
            placeholder={t('common.searchPlaceholder')}
            value={worldSearch}
            onChange={(e) => setWorldSearch(e.target.value)}
          />
                {worldSearch && (
                  <span className={admin.adminSearchResults}>
                    {t('common.searchResults', { count: filteredWorlds.length, total: worlds.length })}
                  </span>
                )}
              </div>
              <Table flush>
              <thead>
                <tr>
                  <th>{t('admin.worlds.columns.name')}</th>
                  <th>{t('admin.worlds.columns.game')}</th>
                  <th>{t('admin.worlds.columns.status')}</th>
                  <th>{t('admin.worlds.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorlds.map((world) => (
                  <tr
                    key={world.id}
                    className={selectedWorld?.id === world.id ? admin.adminRowWorldActive : admin.adminRowWorld}
                    aria-selected={selectedWorld?.id === world.id}
                    onClick={() => handleSelectWorld(world)}
                  >
                    <td>
                      <div className={admin.adminWorldNameCell}>
                        <span className={`font-medium ${admin.adminTextStrong}`}>{world.name}</span>
                        {selectedWorld?.id === world.id && (
                          <Pill solid tone="brand">
                            {t('admin.worlds.sessionsBelow')} ▼
                          </Pill>
                        )}
                      </div>
                    </td>
                    <td>{world.gameName}</td>
                    <td>
                      <Pill tone={activeTone(world.isActive)}>
                        {world.isActive ? t('admin.worlds.active') : t('admin.worlds.inactive')}
                      </Pill>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className={admin.adminRowActions}>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingWorld(world)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button type="button" variant="dangerQuiet" size="sm" disabled={resettingWorldId !== null} onClick={() => void handleResetWorldSave(world)}
                        >
                          {resettingWorldId === world.id ? t('admin.worlds.resetSave.resetting') : t('admin.worlds.resetSave.action')}
                        </Button>
                        <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleDeleteWorld(world)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </>
          )}
        </div>

        {/* ── Sessions panel (shown when a world is selected) ── */}
        {selectedWorld && (
          <div className={admin.adminTableWrapper}>
            <div className={admin.adminTableHeader}>
              <h2 className={admin.adminTableTitle}>
                {t('admin.sessions.sessionsFor', { world: selectedWorld.name })}
              </h2>
              <Button type="button" onClick={() => setShowCreateSessionForm(true)}
              >
                + {t('admin.sessions.createSession')}
              </Button>
            </div>

            {isLoadingSessions ? (
              <div className={admin.adminLoading}>{t('common.loading')}</div>
            ) : sessions.length === 0 ? (
              !error && <EmptyState icon="📅">{t('admin.sessions.noSessions')}</EmptyState>
            ) : (
              <div className={admin.adminSessionsList}>
                {sessions.map((session) => {
                  const status = getSessionStatus(session, interruptedSessionIds.has(session.id));
                  const isSessionScriptDriven = isScriptDriven(session.durationMinutes);
                  return (
                    <div key={session.id} className={`${admin.adminSessionCard} ${status === 'ended' ? admin.adminSessionEnded : ''}`}>
                      <div className={admin.adminSessionHeader}>
                        <div className={admin.adminSessionInfo}>
                          <span className={admin.adminSessionDate}>📅 {session.startTimeUtc ? formatDateTime(session.startTimeUtc) : t('admin.sessions.notScheduled')}</span>
                          <Pill tone={isSessionScriptDriven ? 'info' : 'success'}>
                            ⏱ {isSessionScriptDriven ? t('admin.sessions.sessionScriptDriven') : `${session.durationMinutes} min`}
                          </Pill>
                          <SessionStatusPill status={status} />
                          {session.sessionScriptId && (
                            <span className={admin.adminSessionScript}>📜 {session.sessionScriptId}</span>
                          )}
                          {session.globalAudioMuted && (
                            <span className="text-xs">🔇</span>
                          )}
                        </div>
                        <div className={admin.adminRowActions}>
                          {session.teamAccessCodes.length > 0 && (
                            <>
                              <CopyButton
                value={buildCodesText(session, cohorts, selectedWorld.name)}
                label={t('admin.sessions.copyCode')}
                icon="🔑"
                title={t('admin.sessions.copyCode')}
                              />
                              <CopyButton
                value={buildUrlsText(session, cohorts, selectedWorld.name, getGameClientUrl())}
                label={t('admin.sessions.copyUrl')}
                icon="🔗"
                title={t('admin.sessions.copyUrl')}
                              />
                            </>
                          )}
                          <Button type="button" variant="ghost" size="sm" onClick={() => setEditingSession(session)}
                          >
                            {t('common.edit')}
                          </Button>
                          <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleDeleteSession(session)}
                          >
                            {t('common.delete')}
                          </Button>
                        </div>
                      </div>
                      {/* Cohorts */}
                      <div className={admin.adminSessionCohorts}>
                        👥 {session.cohortIds.join(', ') || '—'}
                      </div>
                      {/* Phase timeline */}
                      {session.startTimeUtc ? (
                        <SessionTimeline
                          startUtc={session.startTimeUtc}
                          lengthMinutes={sessionLengthMinutes(session.durationMinutes)}
                          scriptDriven={isSessionScriptDriven}
                          className={admin.adminSessionTimeline}
                        />
                      ) : null}
                      {/* Access codes */}
                      {session.teamAccessCodes.length > 0 && (
                        <div className={admin.adminSessionCodes}>
                          🔑 {session.teamAccessCodes.map((c) => {
                            const name = cohorts.find((co) => co.id === c.cohortId)?.name ?? c.cohortId;
                            return `${name}: ${String(c.code).padStart(4, '0')}`;
                          }).join('  ·  ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {showCreateWorldForm && (
        <WorldForm
          onClose={() => setShowCreateWorldForm(false)}
          onSaved={handleWorldSaved}
        />
      )}

      {editingWorld && (
        <WorldForm
          world={editingWorld}
          onClose={() => setEditingWorld(null)}
          onSaved={handleWorldSaved}
        />
      )}

      {(showCreateSessionForm || editingSession) && selectedWorld && (
        <SessionForm
          world={selectedWorld}
          session={editingSession ?? undefined}
          cohorts={cohorts}
          onClose={() => { setShowCreateSessionForm(false); setEditingSession(null); }}
          onSaved={handleSessionSaved}
        />
      )}
    </div>
  );
}

// ── Session copy-text helpers ─────────────────────────────────────────────────

const TEST_PLAYER_NAMES = ['TestUser1', 'TestUser2', 'TestUser3', 'TestUser4', 'TestUser5'];



function formatDur(minutes: number): string {
  if (minutes <= 0) return 'session-script-driven';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function buildCodesText(session: SessionItem, cohorts: CohortItem[], worldName: string): string {
  const date = session.startTimeUtc ? formatDateTime(session.startTimeUtc) : '—';
  const dur = formatDur(session.durationMinutes ?? 0);
  let text = `🔑 Team Access Codes — ${worldName}  |  ${date}  (${dur})\n\n`;
  for (const entry of session.teamAccessCodes) {
    const cohortName = cohorts.find((c) => c.id === entry.cohortId)?.name ?? entry.cohortId;
    text += `${cohortName}: ${String(entry.code).padStart(4, '0')}\n`;
  }
  return text.trimEnd();
}

function buildUrlsText(
  session: SessionItem,
  cohorts: CohortItem[],
  worldName: string,
  gameClientUrl: string,
): string {
  const date = session.startTimeUtc ? formatDateTime(session.startTimeUtc) : '—';
  const dur = formatDur(session.durationMinutes ?? 0);
  const base = gameClientUrl.replace(/\/$/, '');
  let text = `✈️ Session Links — ${worldName}  |  ${date}  (${dur})\n\n`;

  for (const entry of session.teamAccessCodes) {
    const cohortName = cohorts.find((c) => c.id === entry.cohortId)?.name ?? entry.cohortId;
    text += `${cohortName}:\n`;
    for (const playerName of TEST_PLAYER_NAMES) {
      // URL format: ?mode=session-link&session=<id>&cohort=<cohortId>&name=<displayName>
      // Player ID is derived automatically on arrival from cohort + sanitized name
      const params = new URLSearchParams({
        mode:    'session-link',
        session: session.externalId,
        cohort:  entry.cohortId,
        name:    playerName,
        lang:    currentUiLanguage(),
      });
      text += `• ${playerName}: ${base}?${params.toString()}\n`;
    }
    text += '\n';
  }

  return text.trimEnd();
}

// ── Copy Button helper ────────────────────────────────────────────────────────

interface CopyButtonProps {
  value: string;
  label: string;
  icon?: string;
  title?: string;
}

function CopyButton({ value, label, icon = '📋', title }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Button type="button" variant={copied ? 'success' : 'ghost'} size="sm" className={admin.adminSessionCopyBtn} onClick={() => void handleCopy()}
      title={title ?? label}
    >
      {copied ? '✓' : icon} {label}
    </Button>
  );
}

// ── World form (create / edit) ────────────────────────────────────────────────

interface WorldFormProps {
  world?: WorldItem;
  onClose: () => void;
  onSaved: () => void;
}

function WorldForm({ world, onClose, onSaved }: WorldFormProps) {
  const { t } = useTranslation();
  const isEditing = !!world;

  // Games dropdown (only needed on create) and Local saves dropdown.
  const gamesQuery = useAdminGameList(!isEditing);
  const games = gamesQuery.data ?? [];
  const isLoadingGames = gamesQuery.isLoading && !isEditing;
  const localSavesQuery = useAdminLocalSaves();
  const localSaves: LocalSaveItem[] = localSavesQuery.data ?? [];
  const isLoadingSaves = localSavesQuery.isLoading;

  // Form state
  const [selectedGameName, setSelectedGameName] = useState(world?.gameName ?? '');
  const [worldName, setWorldName] = useState(world?.name ?? '');
  const [isActive, setIsActive] = useState(world?.isActive ?? true);

  // Pre-select ponder-flight-game once the games list arrives.
  if (!isEditing && !selectedGameName && games.length > 0) {
    const def = games.find((g) => g.name === 'ponder-flight-game');
    if (def) {
      // Defer to next tick so this isn't a setState during render.
      queueMicrotask(() => setSelectedGameName(def.name));
    }
  }

  // Config fields
  const existingConfig = world?.configJson as Record<string, unknown> | undefined;
  const [startingSetup, setStartingSetup] = useState<WorldStartingSetup>(() => startingSetupForConfig(existingConfig));
  const selectedSaveId = startingSetup.startsWith('save:') ? startingSetup.slice('save:'.length) : '';

  const createWorld = useCreateWorld();
  const updateWorld = useUpdateWorld();
  const isSubmitting = createWorld.isPending || updateWorld.isPending;
  // The game's select renders its query failure directly below.
  const [error, setError] = useState<FormFailure | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const configJson = configForStartingSetup(startingSetup, existingConfig);
      if (isEditing && world) {
        await updateWorld.mutateAsync({ id: world.id, payload: { name: worldName, isActive, configJson } });
      } else {
        await createWorld.mutateAsync({ gameName: selectedGameName, worldName, configJson });
      }
      onSaved();
    } catch (e) {
      setError(saveFailure(t, e, 'world'));
    }
  };

  const formatSaveLabel = (s: LocalSaveItem) => t('admin.worlds.form.localSaveLabel', {
    saveId: s.saveId, worldName: s.worldName, ticks: s.gameTicks, teams: s.teamCount,
  });

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={isEditing ? t('admin.worlds.editWorld') : t('admin.worlds.createWorld')}>
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}
        <form onSubmit={handleSubmit}>

          {/* Game selector (create only) */}
          {!isEditing && (
            <FormBlock label={t('admin.worlds.form.gameName')} required>
              {isLoadingGames ? (
                <div className={`${admin.adminLoading} ${admin.adminLoadingInline}`}>{t('common.loading')}</div>
              ) : games.length === 0 ? (
                <FormMessage>{t('admin.worlds.form.gamesLoadError')}</FormMessage>
              ) : (
                <Select
                value={selectedGameName}
                onChange={(e) => setSelectedGameName(e.target.value)}
                  required
                >
                  <option value="">{t('admin.worlds.form.chooseGame')}</option>
                  {games.map((g) => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                </Select>
              )}
</FormBlock>
          )}

          {/* World name */}
          <FormField label={t('admin.worlds.form.worldName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={worldName}
                onChange={(e) => setWorldName(e.target.value)}
                placeholder="World_1"
                required
              />
            )}
          </FormField>

          {/* Active (edit only) */}
          {isEditing && (
            <CheckRow spaced>
              <Checkbox
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              {t('admin.worlds.form.active')}
            </CheckRow>
          )}

          <FormField label={t('admin.worlds.form.startingSetup')} hint={t('admin.worlds.form.startingSetupHint')}>
            {(field) => (
              <Select {...field}
                value={startingSetup}
                onChange={(e) => setStartingSetup(e.target.value as WorldStartingSetup)}
              >
                <option value="starter">{t('admin.worlds.form.starterAirlines')}</option>
                <option value="empty">{t('admin.worlds.form.emptyWorld')}</option>
                {selectedSaveId && !localSaves.some((s) => s.saveId === selectedSaveId) && (
                  <option value={startingSetup}>{t('admin.worlds.form.selectedSave', { saveId: selectedSaveId })}</option>
                )}
                {localSaves.map((s) => (
                  <option key={s.saveId} value={`save:${s.saveId}`}>
                    {formatSaveLabel(s)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          {isLoadingSaves && <span className={admin.adminFormHint}>{t('common.loading')}</span>}
          {localSaves.length === 0 && !isLoadingSaves && (
            <span className={admin.adminFormHint}>
              {t(localSavesQuery.isError ? 'admin.worlds.form.savesLoadError' : 'admin.worlds.form.noSavesAvailable')}
            </span>
          )}

          <div className={admin.adminFormActions}>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || (!isEditing && !selectedGameName)}>
              {isSubmitting ? t('common.loading') : (isEditing ? t('common.save') : t('common.create'))}
            </Button>
          </div>
        </form>
    </Modal>
  );
}

// ── Session form (create / edit) ──────────────────────────────────────────────

interface SessionFormProps {
  world: WorldItem;
  session?: SessionItem;
  cohorts: CohortItem[];
  onClose: () => void;
  onSaved: () => void;
}

function SessionForm({ world, session, cohorts, onClose, onSaved }: SessionFormProps) {
  const { t } = useTranslation();

  // The inputs hold the start's local day and time, as they are read back.
  const [startDate, setStartDate] = useState(
    session && session.startTimeUtc ? localDateInput(session.startTimeUtc) : localDateInput(new Date()),
  );
  const [startTime, setStartTime] = useState(
    session && session.startTimeUtc ? localTimeInput(session.startTimeUtc) : '09:00',
  );
  const [duration, setDuration] = useState(session?.durationMinutes ?? 60);
  const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>(session?.cohortIds ?? []);
  const [sessionScriptId, setSessionScriptId] = useState(session?.sessionScriptId ?? '');
  const [languageCode, setLanguageCode] = useState(session?.languageCode ?? '');
  const [globalAudioMuted, setGlobalAudioMuted] = useState(session?.globalAudioMuted ?? false);
  const createSession = useCreateSession();
  const updateSession = useUpdateSession();
  const isSubmitting = createSession.isPending || updateSession.isPending;
  const [error, setError] = useState<FormFailure | null>(null);
  // The message sits above Save, brought into sight with it as it comes.
  const { messageRef, actionsRef } = useMessageInSight(error);
  const sessionScriptsQuery = useAdminScripts();
  const sessionScripts: SessionScriptItem[] = sessionScriptsQuery.data ?? [];
  const isLoadingSessionScripts = sessionScriptsQuery.isLoading;

  const selectedSessionScript = sessionScripts.find((e) => e.id === sessionScriptId) ?? null;
  // A script's own estimate, said beside the timing as an estimate where its
  // steps give one; the timing is the session's length, as the sessions list
  // times it (sessionLengthMinutes). A script-driven session has no length of
  // its own: the one length the form saves and times it by.
  const estimatedMinutes = selectedSessionScript ? estimateSessionScriptDurationMinutes(selectedSessionScript.steps) : null;
  const durationMinutes = sessionScriptId ? 0 : duration;
  // The start the inputs name, read in local time as they are written: the
  // preview's timeline and the save both take it.
  const startUtc = startDate && startTime ? instantFromInputs(startDate, startTime) : null;

  const toggleCohort = (id: string) => {
    setSelectedCohortIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCohortIds.length === 0) {
      setError({ detail: t('admin.sessions.form.cohortsRequired') });
      return;
    }
    // Both inputs are required, so the browser stops a form without them first.
    if (startUtc === null) return;
    setError(null);
    try {
      const payload: UpsertSessionPayload = {
        gameName: world.gameName,
        worldName: world.name,
        externalId: session?.externalId ?? crypto.randomUUID(),
        startTimeUtc: startUtc,
        durationMinutes,
        cohortIds: selectedCohortIds,
        sessionScriptId: sessionScriptId || undefined,
        languageCode: languageCode || undefined,
        globalAudioMuted,
      };
      await (session ? updateSession.mutateAsync(payload) : createSession.mutateAsync(payload));
      onSaved();
    } catch (e) {
      // A session that would start after its campaign's end is refused, and said in the admin's words.
      setError(saveFailure(t, e, 'session'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={session ? t('admin.sessions.editSession') : t('admin.sessions.createSession')}>
        <form onSubmit={handleSubmit}>

          {/* Date + Time in two columns */}
          <div className={admin.adminFormRow2col}>
            <FormField label={t('admin.sessions.form.startDate')} required>
              {(field) => (
                <Input {...field}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              )}
            </FormField>
            <FormField label={t('admin.sessions.form.startTimeLabel')} required>
              {(field) => (
                <Input {...field}
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              )}
            </FormField>
          </div>

          {/* Session Script dropdown */}
          <FormBlock label={t('admin.sessions.form.sessionScriptId')}>
            {isLoadingSessionScripts ? (
              <div className={`${admin.adminLoading} ${admin.adminLoadingInline}`}>{t('common.loading')}</div>
            ) : (
              <Select
                value={sessionScriptId}
                onChange={(e) => setSessionScriptId(e.target.value)}
              >
                <option value="">{t('admin.sessions.form.noSessionScript')}</option>
                {sessionScripts.map((ss) => (
                  <option key={ss.id} value={ss.id}>{ss.id}</option>
                ))}
              </Select>
            )}
            {!isLoadingSessionScripts && sessionScripts.length === 0 && (
              <span className={admin.adminFormHint}>
                {t(sessionScriptsQuery.isError ? 'admin.sessions.form.sessionScriptsLoadError' : 'admin.sessions.form.noSessionScriptsAvailable')}
              </span>
            )}
</FormBlock>

          {/* Duration — hidden when session script selected; replaced by info panel */}
          {sessionScriptId ? (
            <div className={admin.adminSessionScriptNote}>
              <p className={admin.adminSessionScriptNoteTitle}>
                📜 {t('admin.sessions.form.sessionScriptDurationNote')}
              </p>
              {estimatedMinutes !== null && (
                <p>{t('admin.sessions.form.sessionScriptDurationEstimate', { minutes: estimatedMinutes })}</p>
              )}
              <p>⚠ {t('admin.sessions.form.sessionScriptFallback', { minutes: SESSION_SCRIPT_FALLBACK_DURATION_MINUTES })}</p>
            </div>
          ) : (
            <FormBlock label={t('admin.sessions.form.duration')} required>
              <div className={admin.adminFormDurationRow}>
                <Input
                  type="number"
                  className={admin.adminInputDuration}
                  value={duration}
                  onChange={(e) => setDuration(Math.max(10, Math.min(480, Number(e.target.value))))}
                  min={10}
                  max={480}
                  required
                />
                <span className={`${admin.adminFormHint} ${admin.adminFormHintLeading}`}>
                  {t('admin.sessions.form.durationMinutes')}
                  {duration >= 60 && ` (${Math.floor(duration / 60)}h${duration % 60 ? ` ${duration % 60}m` : ''})`}
                </span>
              </div>
            </FormBlock>
          )}

          {/* Lobby timing preview */}
          {startUtc !== null && (
            <div className={admin.adminSessionLobbyPreview}>
              <p className={admin.adminSessionLobbyTitle}>
                {t('admin.sessions.form.lobbyTimingTitle')}
              </p>
              <SessionTimeline
                startUtc={startUtc}
                lengthMinutes={sessionLengthMinutes(durationMinutes)}
                scriptDriven={!!sessionScriptId}
              />
            </div>
          )}

          {/* Cohorts */}
          <FormBlock
            label={
              <>
                {t('admin.sessions.form.cohorts')}
                <RequiredMark />
                {selectedCohortIds.length > 0 && (
                  <span className={admin.adminLabelCount}>
                    ({selectedCohortIds.length} {t('admin.sessions.form.selected')})
                  </span>
                )}
              </>
            }
          >
            <div className={admin.adminPickList}>
              {cohorts.length === 0 ? (
                <EmptyState inline>{t('admin.sessions.form.noActiveCohorts')}</EmptyState>
              ) : (
                cohorts.map((cohort) => (
                  <CheckRow key={cohort.id} className={admin.adminPickRow}>
                    <Checkbox
                      checked={selectedCohortIds.includes(cohort.name)}
                      onChange={() => toggleCohort(cohort.name)}
                    />
                    <span className={`text-sm ${admin.adminTextStrong}`}>{cohort.name}</span>
                    {cohort.description && (
                      <span className={`text-xs ${admin.adminTextMuted}`}>— {cohort.description}</span>
                    )}
                  </CheckRow>
                ))
              )}
            </div>
          </FormBlock>

          {/* Language */}
          <FormField label={t('admin.sessions.form.language')}>
            {(field) => (
              <Select {...field}
                value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value)}
              >
                <option value="">{t('admin.sessions.form.autoDetect')}</option>
                <option value="pt">Português (pt)</option>
                <option value="en">English (en)</option>
                <option value="es">Español (es)</option>
              </Select>
            )}
          </FormField>

          {/* Audio mute — Dev / Test Features */}
          <FormBlock label={<>🧪 {t('admin.sessions.form.devTestFeatures')}</>}>
            <CheckRow>
              <Checkbox
                checked={globalAudioMuted}
                onChange={(e) => setGlobalAudioMuted(e.target.checked)}
              />
              <span>🔇 {t('admin.sessions.form.muteAudio')}</span>
            </CheckRow>
          </FormBlock>

          {/* Above Save, kept in sight as it comes (useMessageInSight): the form's top scrolls out of sight. */}
          {error && (
            <div ref={messageRef} tabIndex={-1} role="group" aria-label={error.title ?? error.detail ?? undefined}>
              <FormMessage title={error.title}>{error.detail}</FormMessage>
            </div>
          )}
          <div ref={actionsRef} className={admin.adminFormActions}>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </div>
        </form>
    </Modal>
  );
}
